/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { AppErrorBoundary } from '../components';
import { AuthRoute } from './auth';
import { ROUTER_URL } from './constants';
import { NotFound } from './notFound';

// Layout
const MainLayout = lazy(() =>
  import('../layout/main').then(({ WrapMainLayout: Component }) => ({
    default: Component,
  }))
);
const AuthLayout = lazy(() =>
  import('../layout/auth').then(({ AuthLayout: Component }) => ({
    default: Component,
  }))
);
// FactoryManagement page
const FactoryManagement = lazy(() =>
  import('../pages/factory').then(({ FactoryManagement: Component }) => ({
    default: Component,
  }))
);
const FactoryManagementJobsDisplay = lazy(() =>
  import('../pages/factory/jobsDisplay').then(
    ({ FactoryManagementJobsDisplay: Component }) => ({
      default: Component,
    })
  )
);
const FactoryManagementJobsSorting = lazy(() =>
  import('../pages/factory/jobsSorting').then(
    ({ FactoryManagementJobsSorting: Component }) => ({
      default: Component,
    })
  )
);
const FactoryManagementViewLogs = lazy(() =>
  import('../pages/factory/viewLogs').then(
    ({ FactoryManagementViewLog: Component }) => ({
      default: Component,
    })
  )
);
// Login
const Login = lazy(() =>
  import('../pages/login').then(({ Login: Component }) => ({
    default: Component,
  }))
);
// User
const UserManagement = lazy(() =>
  import('../pages/user').then(({ UserManagement: Component }) => ({
    default: Component,
  }))
);
const RoleManagement = lazy(() =>
  import('../pages/role').then(({ RoleManagement: Component }) => ({
    default: Component,
  }))
);
const AddRole = lazy(() =>
  import('../pages/role/addRole').then(({ AddRole: Component }) => ({
    default: Component,
  }))
);
const UserProfile = lazy(() =>
  import('../pages/userProfile').then(({ UserProfile: Component }) => ({
    default: Component,
  }))
);
// Major Item
const ItemManagement = lazy(() =>
  import('../pages/itemManagement').then(({ ItemManagement: Component }) => ({
    default: Component,
  }))
);
const ItemManagementImportDate = lazy(() =>
  import('../pages/itemManagement/importDate').then(
    ({ ItemManagementImportDate: Component }) => ({
      default: Component,
    })
  )
);
const ItemManagementImportDateDetail = lazy(() =>
  import('../pages/itemManagement/importDate/detail').then(
    ({ ItemManagementDetail: Component }) => ({
      default: Component,
    })
  )
);
const ItemManagementUnitDate = lazy(() =>
  import('../pages/itemManagement/unit').then(
    ({ ItemManagementUnitDate: Component }) => ({
      default: Component,
    })
  )
);
const ItemManagementViewLogs = lazy(() =>
  import('../pages/itemManagement/viewLogs').then(
    ({ ItemManagementViewLogs: Component }) => ({
      default: Component,
    })
  )
);
// Engineering Management
const EngineeringManagement = lazy(() =>
  import('../pages/engineeringManagement').then(
    ({ EngineeringManagement: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringMajorProjects = lazy(() =>
  import('../pages/engineeringManagement/majorProjects').then(
    ({ MajorProjects: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringSpecialProjects = lazy(() =>
  import('../pages/engineeringManagement/specialProjects').then(
    ({ SpecialProjects: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringItemFault = lazy(() =>
  import('../pages/engineeringManagement/itemFault').then(
    ({ ItemFaultReporting: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringItemFaultDetail = lazy(() =>
  import('../pages/engineeringManagement/itemFault/detail').then(
    ({ ItemFaultsDetail: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringAddItemFault = lazy(() =>
  import('../pages/engineeringManagement/itemFault/add').then(
    ({ AddItemFault: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringProductionFault = lazy(() =>
  import('../pages/engineeringManagement/productionFault').then(
    ({ ProductionFaultReporting: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringProductionFaultDetail = lazy(() =>
  import('../pages/engineeringManagement/productionFault/detail').then(
    ({ ProductionFaultsDetail: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringAddProductionFault = lazy(() =>
  import('../pages/engineeringManagement/productionFault/add').then(
    ({ AddItemFault: Component }) => ({
      default: Component,
    })
  )
);
const UploadCostSpecialProject = lazy(() =>
  import('../pages/engineeringManagement/specialProjects/uploadCost').then(
    ({ UploadCostSpecialProject: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringSummary = lazy(() =>
  import('../pages/engineeringManagement/summary').then(
    ({ Summary: Component }) => ({
      default: Component,
    })
  )
);
const MajorProjectsDetail = lazy(() =>
  import('../pages/engineeringManagement/majorProjectsDetail').then(
    ({ MajorProjectsDetail: Component }) => ({
      default: Component,
    })
  )
);
const SpecialProjectsDetail = lazy(() =>
  import('../pages/engineeringManagement/specialProjects/detail').then(
    ({ SpecialProjectsDetail: Component }) => ({
      default: Component,
    })
  )
);
const EngineeringManagementViewLogs = lazy(() =>
  import('../pages/engineeringManagement/components/viewLogs').then(
    ({ EngineeringManagementViewLogs: Component }) => ({
      default: Component,
    })
  )
);
// Subcontractor
const SubcontractorManagement = lazy(() =>
  import('../pages/subcontractor').then(
    ({ SubcontractorManagement: Component }) => ({
      default: Component,
    })
  )
);
const SubcontractorAll = lazy(() =>
  import('../pages/subcontractor/all').then(
    ({ SubcontractorAll: Component }) => ({
      default: Component,
    })
  )
);
const SubcontractorDetail = lazy(() =>
  import('../pages/subcontractor/detail').then(
    ({ SubcontractorDetail: Component }) => ({
      default: Component,
    })
  )
);
const SubcontractorUnit = lazy(() =>
  import('../pages/subcontractor/unit').then(
    ({ Subcontractor: Component }) => ({
      default: Component,
    })
  )
);
const SubcontractorViewLogs = lazy(() =>
  import('../pages/subcontractor/viewLogs').then(
    ({ SubcontractorViewLogs: Component }) => ({
      default: Component,
    })
  )
);
// Company
const CompanyManagement = lazy(() =>
  import('../pages/company').then(({ CompanyManagement: Component }) => ({
    default: Component,
  }))
);
// Engineering Teams
const EngineeringTeam = lazy(() =>
  import('../pages/engineeringTeam').then(({ EngineeringTeam: Component }) => ({
    default: Component,
  }))
);
// Settings
const Settings = lazy(() =>
  import('../pages/settings').then(({ Settings: Component }) => ({
    default: Component,
  }))
);
const EditSettings = lazy(() =>
  import('../pages/settings/editSetting').then(
    ({ EditSetting: Component }) => ({
      default: Component,
    })
  )
);
// Rate Setting
const RateSetting = lazy(() =>
  import('../pages/rateSetting').then(({ RateSetting: Component }) => ({
    default: Component,
  }))
);
const EditTableRate = lazy(() =>
  import('../pages/rateSetting/editTableRate').then(
    ({ EditTableRate: Component }) => ({
      default: Component,
    })
  )
);
// Special Project
const SpecialProject = lazy(() =>
  import('../pages/specialProject').then(({ SpecialProject: Component }) => ({
    default: Component,
  }))
);
const SpecialProjectDetail = lazy(() =>
  import('../pages/specialProject/detail').then(
    ({ SpecialProjectDetail: Component }) => ({
      default: Component,
    })
  )
);
// Easter Egg
const EasterEgg = lazy(() =>
  import('../pages/easterEgg').then(({ EasterEgg: Component }) => ({
    default: Component,
  }))
);

export const router = createBrowserRouter([
  {
    path: ROUTER_URL.Home,
    errorElement: <NotFound />,
    element: (
      <AppErrorBoundary>
        <MainLayout />
      </AppErrorBoundary>
    ),
    children: [
      {
        element: (
          <AuthRoute
            permissions={[
              'factoryManagement.viewJobsDisplay',
              'factoryManagement.viewJobsSorting',
              'factoryManagement.editJobsSorting',
              'factoryManagement.editProcessStatus',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.FactoryManagement,
            element: <FactoryManagement />,
            children: [
              {
                element: (
                  <AuthRoute
                    permissions={['factoryManagement.viewJobsDisplay']}
                  />
                ),
                children: [
                  {
                    path: ROUTER_URL.FactoryManagementJobsDisplay,
                    element: <FactoryManagementJobsDisplay />,
                  },
                ],
              },
              {
                element: (
                  <AuthRoute
                    permissions={['factoryManagement.viewJobsSorting']}
                  />
                ),
                children: [
                  {
                    path: ROUTER_URL.FactoryManagementJobsSorting,
                    element: <FactoryManagementJobsSorting />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTER_URL.FactoryManagementViewLog,
            element: <FactoryManagementViewLogs />,
          },
        ],
      },
      {
        path: ROUTER_URL.UserProfile,
        element: <UserProfile />,
      },
      {
        element: (
          <AuthRoute
            permissions={[
              'item.import.date.view',
              'item.qc.date.view',
              'item.delivered.date.view',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.ItemManagement,
            children: [
              {
                path: ROUTER_URL.ItemManagement,
                element: <ItemManagement />,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate to={ROUTER_URL.ItemManagementImportDate} />
                    ),
                  },
                  {
                    path: ROUTER_URL.ItemManagementImportDate,
                    element: <ItemManagementImportDate />,
                  },
                  {
                    path: ROUTER_URL.ItemManagementQCDate,
                    element: <ItemManagementUnitDate />,
                  },
                  {
                    path: ROUTER_URL.ItemManagementDeliveredDate,
                    element: <ItemManagementUnitDate />,
                  },
                ],
              },
              {
                path: ROUTER_URL.ItemManagementViewLogs,
                element: <ItemManagementViewLogs />,
              },
              {
                path: ROUTER_URL.ItemManagementImportDateDetail,
                element: <ItemManagementImportDateDetail />,
              },
            ],
          },
        ],
      },
      {
        element: (
          <AuthRoute
            permissions={[
              'engineering.major.viewYourAssociatedTeamNoTeam',
              'engineering.major.viewAllTeams',
              'engineering.special.viewYourAssociatedTeamNoTeam',
              'engineering.special.viewAllTeams',
              'engineering.itemFault.viewYourAssociatedTeamNoTeam',
              'engineering.itemFault.viewAllTeams',
              'engineering.productionFault.viewYourAssociatedTeamNoTeam',
              'engineering.productionFault.viewAllTeams',
              'engineering.summary.viewSummaryTab',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.EngineeringManagement,
            element: <EngineeringManagement />,
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to={ROUTER_URL.EngineeringManagementMajorProjects}
                  />
                ),
              },
              {
                path: ROUTER_URL.EngineeringManagementMajorProjects,
                element: <EngineeringMajorProjects />,
              },
              {
                path: ROUTER_URL.EngineeringManagementSpecialProjects,
                element: <EngineeringSpecialProjects />,
              },
              {
                path: ROUTER_URL.EngineeringManagementItemFault,
                element: <EngineeringItemFault />,
              },
              {
                path: ROUTER_URL.EngineeringManagementProductionFault,
                element: <EngineeringProductionFault />,
              },
              {
                path: ROUTER_URL.EngineeringManagementSummary,
                element: <EngineeringSummary />,
              },
            ],
          },
          {
            path: ROUTER_URL.EngineeringManagementViewLogs,
            element: <EngineeringManagementViewLogs />,
          },
          {
            path: ROUTER_URL.EngineeringManagementMajorProjectsDetail,
            element: <MajorProjectsDetail />,
          },
          {
            path: ROUTER_URL.EngineeringManagementSpecialProjectsDetail,
            element: <SpecialProjectsDetail />,
          },
          {
            path: ROUTER_URL.EngineeringManagementItemFaultsDetail,
            element: <EngineeringItemFaultDetail />,
          },
          {
            path: ROUTER_URL.EngineeringManagementAddItemFault,
            element: <EngineeringAddItemFault />,
          },
          {
            path: ROUTER_URL.EngineeringManagementProductionFaultsDetail,
            element: <EngineeringProductionFaultDetail />,
          },
          {
            path: ROUTER_URL.EngineeringManagementAddProductionFault,
            element: <EngineeringAddProductionFault />,
          },
          {
            path: ROUTER_URL.EngineeringManagementSpecialProjectUploadCost,
            element: <UploadCostSpecialProject />,
          },
        ],
      },
      {
        element: (
          <AuthRoute
            permissions={[
              'user.office.viewList',
              'user.subcontractor.viewList',
              'user.production.viewList',
              'user.engineering.viewList',
              'role.permissions.view',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.User,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTER_URL.UserManagement} />,
              },
              {
                element: (
                  <AuthRoute
                    permissions={[
                      'user.office.viewList',
                      'user.subcontractor.viewList',
                      'user.production.viewList',
                      'user.engineering.viewList',
                    ]}
                  />
                ),
                children: [
                  {
                    path: ROUTER_URL.UserManagement,
                    element: <UserManagement />,
                  },
                ],
              },
              {
                element: <AuthRoute permissions={['role.permissions.view']} />,
                children: [
                  {
                    path: ROUTER_URL.RoleManagement,
                    element: <RoleManagement />,
                  },
                  {
                    path: ROUTER_URL.RoleManagementDetail,
                    element: <AddRole isView />,
                  },
                ],
              },
              {
                element: <AuthRoute permissions={['role.permissions.add']} />,
                children: [
                  {
                    path: ROUTER_URL.RoleManagementAdd,
                    element: <AddRole />,
                  },
                ],
              },
              {
                element: <AuthRoute permissions={['role.permissions.edit']} />,
                children: [
                  {
                    path: ROUTER_URL.RoleManagementEdit,
                    element: <AddRole />,
                  },
                ],
              },
              {
                element: (
                  <AuthRoute
                    permissions={[
                      'engineering.teams.team.view',
                      'engineering.teams.classification.view',
                    ]}
                  />
                ),
                children: [
                  {
                    path: ROUTER_URL.EngineeringTeam,
                    element: <EngineeringTeam />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: (
          <AuthRoute
            permissions={[
              'subcontractor.viewAllSubcontractors',
              'subcontractor.viewAssociatedSubcontractors',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.Subcontractor,
            element: <SubcontractorManagement />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTER_URL.SubcontractorAll} />,
              },
              {
                path: ROUTER_URL.SubcontractorAll,
                element: <SubcontractorAll />,
              },
              {
                path: ROUTER_URL.SubcontractorLaserCutting,
                element: <SubcontractorUnit />,
              },
              {
                path: ROUTER_URL.SubcontractorInsulationCutting,
                element: <SubcontractorUnit />,
              },
              {
                path: ROUTER_URL.SubcontractorRollForming,
                element: <SubcontractorUnit />,
              },
              {
                path: ROUTER_URL.SubcontractorFolding,
                element: <SubcontractorUnit />,
              },
            ],
          },
          {
            path: ROUTER_URL.SubcontractorViewLogs,
            element: <SubcontractorViewLogs />,
          },
          {
            path: ROUTER_URL.SubcontractorLaserCuttingDetail,
            element: <SubcontractorDetail />,
          },
          {
            path: ROUTER_URL.SubcontractorInsulationCuttingDetail,
            element: <SubcontractorDetail />,
          },
          {
            path: ROUTER_URL.SubcontractorRollFormingDetail,
            element: <SubcontractorDetail />,
          },
          {
            path: ROUTER_URL.SubcontractorFoldingDetail,
            element: <SubcontractorDetail />,
          },
        ],
      },
      {
        element: <AuthRoute permissions={['company.view']} />,
        children: [
          {
            path: ROUTER_URL.CompanyManagement,
            children: [
              {
                path: ROUTER_URL.CompanyManagement,
                element: <CompanyManagement />,
              },
            ],
          },
        ],
      },
      {
        element: <AuthRoute permissions={['setting.view']} />,
        children: [
          {
            path: ROUTER_URL.Settings,
            element: <Settings />,
          },
          {
            element: <AuthRoute permissions={['setting.edit']} />,
            children: [
              {
                path: ROUTER_URL.EditSettings,
                element: <EditSettings />,
              },
            ],
          },
        ],
      },
      {
        element: <AuthRoute permissions={['setting.view']} />,
        children: [
          {
            path: ROUTER_URL.RateSetting,
            element: <RateSetting />,
          },
          {
            element: <AuthRoute permissions={['setting.edit']} />,
            children: [
              {
                path: ROUTER_URL.EditRateSettings,
                element: <EditTableRate />,
              },
            ],
          },
        ],
      },
      {
        element: (
          <AuthRoute
            permissions={[
              'special.project.list.uploadFiles',
              'special.project.list.deleteFiles',
              'special.project.list.deleteExcelList',
            ]}
          />
        ),
        children: [
          {
            path: ROUTER_URL.SpecialProjectList,
            children: [
              {
                path: ROUTER_URL.SpecialProjectList,
                element: <SpecialProject />,
              },
              {
                element: (
                  <AuthRoute
                    permissions={['special.project.list.viewDetails']}
                  />
                ),
                children: [
                  {
                    path: ROUTER_URL.SpecialProjectDetail,
                    element: <SpecialProjectDetail />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: ROUTER_URL.EasterEgg,
        element: <EasterEgg />,
      },
    ],
  },
  {
    path: ROUTER_URL.Auth,
    errorElement: <NotFound />,
    element: (
      <AppErrorBoundary>
        <AuthLayout />
      </AppErrorBoundary>
    ),
    children: [
      {
        path: ROUTER_URL.Login,
        element: <Login />,
      },
    ],
  },
]);
